import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled/macro';
import {
  Button,
  SlidingDrawer,
  Typography,
  SvgIcon,
  SvgNames,
  Select,
  tokens,
  Checkbox,
  MultiSelectDropdown,
  PillSwitchButtons,
} from '@sunrun/experience-ui-components';
import { useFeatureFlagEnabled } from '@sunrun/sales-experience-shared/react-feature-flags';
import {
  HierarchyTreeNode,
  TreeList,
} from 'components/atoms/HierarchyTreeNode';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import { useUserHierarchy } from 'data/useUserHierarchy';
import {
  areAllContactIdsSelected,
  buildPipelineHierarchyIndex,
  buildTeamGroupedHierarchy,
  computePipelineNodeRenderStates,
  filterHierarchyByContactId,
  filterSelectableReports,
  getPickerContactIdsInScope,
  hasDirectReports,
  toggleAllContactIds,
  toggleNodeContactIds,
  type SalesRepDisplayMode,
} from 'helpers/pipelineHierarchySelection';
import { PipelineControls, SalesRepTeamScope } from 'types/prospect';
import type { UserHierarchyNode } from 'types/userHierarchy';
import { useUserProfile } from 'providers/UserProfile';
import { useSalesReps } from 'providers/Pipeline';

type SalesRepScopeOption = {
  value: SalesRepTeamScope;
  label: string;
};

const BASE_SALES_REP_SCOPE_OPTIONS: SalesRepScopeOption[] = [
  { value: 'directReports', label: 'My direct reports' },
  {
    value: 'office',
    label: 'All in my office (matching sales team)',
  },
];

const HIERARCHY_SCOPE_OPTION: SalesRepScopeOption = {
  value: 'hierarchy',
  label: 'Workday Hierarchy',
};

const SALES_REP_DISPLAY_OPTIONS: {
  value: SalesRepDisplayMode;
  label: string;
}[] = [
  { value: 'teams', label: 'Teams View' },
  { value: 'hierarchy', label: 'Hierarchy View' },
];

const NO_SALES_REPS_FOUND_MESSAGE = 'No Sales Reps found';

type PipelineHierarchyPickerProps = {
  hierarchy: UserHierarchyNode;
  selectedContactIds: Set<string>;
  onSelectedContactIdsChange: (next: Set<string>) => void;
  userEmailForHierarchy?: string | null;
  displayMode?: SalesRepDisplayMode;
  maxHeight?: string;
};

type PipelineHierarchySectionProps = {
  filtersOpen: boolean;
  hierarchy: UserHierarchyNode | null;
  loading: boolean;
  error: string | null;
  salesReps: string[];
  hierarchyDisplayMode: SalesRepDisplayMode;
  onSalesRepsChange: (contactIds: string[]) => void;
  onHierarchyDisplayModeChange: (
    mode: SalesRepDisplayMode,
    salesReps: string[],
  ) => void;
  userEmailForHierarchy: string | null;
  defaultContactId?: string;
};

const formatHierarchySelectionCount = (count: number): string => {
  if (count === 1) {
    return '1 Sales Rep selected';
  }
  return `${count} Sales Reps selected`;
};

const PipelineHierarchyPicker = ({
  hierarchy,
  selectedContactIds,
  onSelectedContactIdsChange,
  userEmailForHierarchy = null,
  displayMode = 'teams',
  maxHeight = '240px',
}: PipelineHierarchyPickerProps) => {
  const index = useMemo(
    () => buildPipelineHierarchyIndex(hierarchy),
    [hierarchy],
  );
  const allInScope = useMemo(
    () => getPickerContactIdsInScope(hierarchy, userEmailForHierarchy),
    [hierarchy, userEmailForHierarchy],
  );
  const nodeRenderStates = useMemo(
    () =>
      computePipelineNodeRenderStates(
        hierarchy,
        index,
        selectedContactIds,
        allInScope,
      ),
    [hierarchy, index, selectedContactIds, allInScope],
  );

  const rootEmail = hierarchy.User.Email;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
    displayMode === 'teams' ? new Set() : new Set([rootEmail]),
  );

  useEffect(() => {
    setExpandedIds(
      displayMode === 'teams' ? new Set() : new Set([hierarchy.User.Email]),
    );
  }, [hierarchy, displayMode]);

  const handleToggleExpand = useCallback((nodeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const handleToggleSelection = useCallback(
    (nodeEmail: string) => {
      const node = index.nodeByEmail.get(nodeEmail);
      if (!node) {
        return;
      }
      onSelectedContactIdsChange(
        toggleNodeContactIds(node, index, selectedContactIds),
      );
    },
    [index, onSelectedContactIdsChange, selectedContactIds],
  );

  const handleToggleAll = useCallback(() => {
    onSelectedContactIdsChange(
      toggleAllContactIds(allInScope, selectedContactIds),
    );
  }, [allInScope, onSelectedContactIdsChange, selectedContactIds]);

  if (!hasDirectReports(hierarchy)) {
    return null;
  }

  const allChecked = areAllContactIdsSelected(allInScope, selectedContactIds);

  return (
    <TreeContainer maxHeight={maxHeight}>
      <AllOptionRow>
        <HierarchyCheckboxWrap>
          <Checkbox
            checked={allChecked}
            onChange={handleToggleAll}
            aria-label="All"
          />
        </HierarchyCheckboxWrap>
        <HierarchyRowLabel>
          <Typography size="small" color={tokens.TINTS_OFF_WHITE_10}>
            All
          </Typography>
        </HierarchyRowLabel>
      </AllOptionRow>
      <TreeList depth={0}>
        {filterSelectableReports(hierarchy.Reports || [], index).map(
          (child) => {
            const childEmail = child.User.Email;
            const childState = nodeRenderStates.get(childEmail) ?? {
              checked: false,
              canToggle: false,
              contactCount: 0,
              isManager: false,
            };
            return (
              <HierarchyTreeNode
                key={childEmail}
                node={child}
                depth={0}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
                checked={childState.checked}
                canToggle={childState.canToggle}
                contactCount={childState.contactCount}
                isManager={childState.isManager}
                onToggleSelection={handleToggleSelection}
                nodeRenderStates={nodeRenderStates}
                index={index}
                displayMode={displayMode}
              />
            );
          },
        )}
      </TreeList>
    </TreeContainer>
  );
};

const PipelineHierarchySection = ({
  filtersOpen,
  hierarchy,
  loading,
  error,
  salesReps,
  hierarchyDisplayMode,
  onSalesRepsChange,
  onHierarchyDisplayModeChange,
  userEmailForHierarchy,
  defaultContactId,
}: PipelineHierarchySectionProps) => {
  const [displayMode, setDisplayMode] =
    useState<SalesRepDisplayMode>(hierarchyDisplayMode);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(
    () => new Set(salesReps),
  );
  const prevFiltersOpen = useRef(filtersOpen);

  const scopedHierarchy = useMemo(() => {
    if (!hierarchy) {
      return null;
    }
    return filterHierarchyByContactId(hierarchy);
  }, [hierarchy]);

  const displayHierarchy = useMemo(() => {
    if (!scopedHierarchy) {
      return null;
    }
    if (displayMode === 'hierarchy') {
      return scopedHierarchy;
    }
    if (!userEmailForHierarchy) {
      return scopedHierarchy;
    }
    return buildTeamGroupedHierarchy(scopedHierarchy, userEmailForHierarchy);
  }, [scopedHierarchy, displayMode, userEmailForHierarchy]);

  const defaultSalesReps = (defaultContactId && [defaultContactId]) || [];

  useEffect(() => {
    if (filtersOpen && !prevFiltersOpen.current) {
      setDisplayMode(hierarchyDisplayMode);
      setSelectedContactIds(new Set(salesReps));
    }
    prevFiltersOpen.current = filtersOpen;
  }, [filtersOpen, hierarchyDisplayMode, salesReps]);

  const handleDisplayModeChange = (mode: SalesRepDisplayMode) => {
    setDisplayMode(mode);
    setSelectedContactIds(new Set());
    onHierarchyDisplayModeChange(mode, defaultSalesReps);
  };

  const handleSelectionChange = (next: Set<string>) => {
    setSelectedContactIds(next);
    onSalesRepsChange((next.size > 0 && [...next]) || defaultSalesReps);
  };

  const selectedCount = selectedContactIds.size;

  return (
    <>
      <PillSwitchButtons
        options={SALES_REP_DISPLAY_OPTIONS}
        value={displayMode}
        fullWidth={true}
        size="md"
        onChange={handleDisplayModeChange}
        aria-label="Sales rep display"
      />
      {selectedCount > 0 && (
        <SelectionCountText size="small" color={tokens.TINTS_OFF_WHITE_20}>
          {formatHierarchySelectionCount(selectedCount)}
        </SelectionCountText>
      )}
      <HierarchySection>
        {loading && (
          <HierarchyLoadingSection>
            <SvgIcon
              name={SvgNames.Loading}
              width={24}
              height={24}
              color={tokens.BRAND_HERO_BLUE}
            />
          </HierarchyLoadingSection>
        )}
        {error && (
          <HierarchyError color={tokens.ERROR_60}>{error}</HierarchyError>
        )}
        {!loading &&
          !error &&
          displayHierarchy &&
          (hasDirectReports(displayHierarchy) ? (
            <PipelineHierarchyPicker
              hierarchy={displayHierarchy}
              selectedContactIds={selectedContactIds}
              onSelectedContactIdsChange={handleSelectionChange}
              userEmailForHierarchy={userEmailForHierarchy}
              displayMode={displayMode}
            />
          ) : (
            <EmptyStateText size="small" color={tokens.TINTS_OFF_WHITE_20}>
              {NO_SALES_REPS_FOUND_MESSAGE}
            </EmptyStateText>
          ))}
      </HierarchySection>
    </>
  );
};

const PipelineFilterMenu = ({
  filtersOpen,
  setFiltersOpen,
  controls,
  setControls,
  onApplyControl,
}: {
  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;
  controls: PipelineControls;
  setControls: React.Dispatch<React.SetStateAction<PipelineControls>>;
  onApplyControl: (controls: PipelineControls) => void;
}) => {
  const [salesRepSectionPinned, setSalesRepSectionPinned] = useState(false);
  const [searchParams] = useSearchParams();
  const jwt = searchParams.get('jwt');
  const emailFromParams = searchParams.get('email') ?? '';
  const { userProfile, splatUserType } = useUserProfile();
  const isSalesChatEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.SALES_CHAT_ENABLED,
  );
  const isDirectorPipelineHierarchyEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.DIRECTOR_PIPELINE_HIERARCHY,
  );
  const salesRepScopeOptions = useMemo(
    () => [
      ...BASE_SALES_REP_SCOPE_OPTIONS,
      ...(isDirectorPipelineHierarchyEnabled ? [HIERARCHY_SCOPE_OPTION] : []),
    ],
    [isDirectorPipelineHierarchyEnabled],
  );
  const isHierarchyScope =
    isDirectorPipelineHierarchyEnabled &&
    controls.salesRepTeamScope === 'hierarchy';
  const userEmailForHierarchy =
    emailFromParams.trim() || userProfile?.email?.trim() || null;
  const salesRepScopeSelectValue =
    !isDirectorPipelineHierarchyEnabled &&
    controls.salesRepTeamScope === 'hierarchy'
      ? 'directReports'
      : controls.salesRepTeamScope;

  const { data: salesReps, isLoading: salesRepsLoading } = useSalesReps(
    controls.salesRepTeamScope === 'office',
    filtersOpen && !isHierarchyScope,
  );

  const {
    hierarchy,
    loading: hierarchyLoading,
    error,
  } = useUserHierarchy({
    jwt,
    userEmailForHierarchy,
    enabled:
      filtersOpen && isHierarchyScope && Boolean(jwt && userEmailForHierarchy),
  });

  const scopedHierarchy = useMemo(() => {
    if (!hierarchy) {
      return null;
    }
    return filterHierarchyByContactId(hierarchy);
  }, [hierarchy]);

  const salesRepOptions = useMemo(() => {
    if (!userProfile?.contactId) {
      return [];
    }

    const meOption = {
      id: userProfile.contactId,
      text: 'Me',
      checked: controls.salesReps.includes(userProfile.contactId),
    };

    const otherOptions = (salesReps ?? [])
      .filter((rep) => rep.id !== userProfile.contactId)
      .map((rep) => ({
        id: rep.id,
        text: rep.name,
        checked: controls.salesReps.includes(rep.id),
      }));

    return [meOption, ...otherOptions];
  }, [controls.salesReps, salesReps, userProfile]);

  useEffect(() => {
    if (
      !isDirectorPipelineHierarchyEnabled &&
      controls.salesRepTeamScope === 'hierarchy'
    ) {
      setControls((current) => ({
        ...current,
        salesRepTeamScope: 'directReports',
        salesReps: (userProfile && [userProfile.contactId]) || [],
        hierarchyDisplayMode: 'teams',
      }));
    }
  }, [
    controls.salesRepTeamScope,
    isDirectorPipelineHierarchyEnabled,
    setControls,
    userProfile,
  ]);

  useEffect(() => {
    if (!filtersOpen) {
      setSalesRepSectionPinned(false);
      return;
    }
    if (!isHierarchyScope && salesRepOptions.length > 2) {
      setSalesRepSectionPinned(true);
    }
    if (
      isHierarchyScope &&
      scopedHierarchy != null &&
      hasDirectReports(scopedHierarchy)
    ) {
      setSalesRepSectionPinned(true);
    }
  }, [filtersOpen, isHierarchyScope, salesRepOptions.length, scopedHierarchy]);

  const isTeamScopeLoading = isHierarchyScope
    ? hierarchyLoading
    : salesRepsLoading;
  const showFlatTeamSection =
    !isHierarchyScope &&
    (salesRepsLoading || salesRepOptions.length > 2 || salesRepSectionPinned);
  const showHierarchyTeamSection =
    isHierarchyScope &&
    (hierarchyLoading ||
      Boolean(error) ||
      scopedHierarchy != null ||
      salesRepSectionPinned);
  const showSalesRepTeamSection =
    showFlatTeamSection || showHierarchyTeamSection;
  const handleSalesRepScopeChange = (salesRepTeamScope: SalesRepTeamScope) => {
    setControls((current) => {
      let salesReps: string[] = [];
      if (salesRepTeamScope !== 'hierarchy' && userProfile?.contactId) {
        salesReps = [userProfile.contactId];
      }

      return {
        ...current,
        salesRepTeamScope,
        salesReps,
        hierarchyDisplayMode:
          salesRepTeamScope === 'hierarchy'
            ? 'teams'
            : current.hierarchyDisplayMode,
      };
    });
  };

  const handleApply = () => {
    const controlsToApply =
      isHierarchyScope &&
      controls.salesReps.length === 0 &&
      userProfile?.contactId
        ? { ...controls, salesReps: [userProfile.contactId] }
        : controls;
    onApplyControl(controlsToApply);
    setFiltersOpen(false);
  };

  return (
    <SlidingDrawer
      isOpen={filtersOpen}
      onClose={() => setFiltersOpen(false)}
      closeOnEscape={true}>
      <FilterContainer>
        <FilterHeader>
          <Typography component="h1" size="large">
            Filters
          </Typography>
          <Button
            color="tertiary"
            size="sm"
            iconMiddle={
              <SvgIcon
                name={SvgNames.Close}
                width={24}
                height={24}
                color={tokens.BLACK}></SvgIcon>
            }
            onClick={() => setFiltersOpen(false)}></Button>
        </FilterHeader>
        <FilterBody>
          <FilterField>
            <FilterLabel size={tokens.FONT_SIZE_1}>Sort by</FilterLabel>
            <Select
              options={[
                {
                  label: 'Default',
                  value: 'default',
                },
                {
                  label: 'Recently Updated',
                  value: 'newest',
                },
                {
                  label: 'Oldest',
                  value: 'oldest',
                },
              ]}
              value={controls.sort}
              onChange={(e) =>
                setControls(
                  (current) =>
                    ({
                      ...current,
                      sort: e.target.value,
                    } as PipelineControls),
                )
              }></Select>
          </FilterField>
          {showSalesRepTeamSection && (
            <>
              <FilterField>
                <FilterLabel size={tokens.FONT_SIZE_1}>
                  Sales reps team
                </FilterLabel>
                <Select
                  options={salesRepScopeOptions}
                  value={salesRepScopeSelectValue}
                  disabled={isTeamScopeLoading}
                  onChange={(event) =>
                    handleSalesRepScopeChange(
                      event.target.value as SalesRepTeamScope,
                    )
                  }
                />
              </FilterField>
              {isHierarchyScope && (
                <FilterField>
                  <FilterLabel size={tokens.FONT_SIZE_1}>Sales rep</FilterLabel>
                  <PipelineHierarchySection
                    filtersOpen={filtersOpen}
                    hierarchy={hierarchy}
                    loading={hierarchyLoading}
                    error={error}
                    salesReps={controls.salesReps}
                    hierarchyDisplayMode={controls.hierarchyDisplayMode}
                    onSalesRepsChange={(salesReps) =>
                      setControls((current) => ({ ...current, salesReps }))
                    }
                    onHierarchyDisplayModeChange={(mode, salesReps) =>
                      setControls((current) => ({
                        ...current,
                        hierarchyDisplayMode: mode,
                        salesReps,
                      }))
                    }
                    userEmailForHierarchy={userEmailForHierarchy}
                    defaultContactId={userProfile?.contactId}
                  />
                </FilterField>
              )}
              {!isHierarchyScope && (
                <FilterField>
                  <FilterLabel size={tokens.FONT_SIZE_1}>Sales rep</FilterLabel>
                  {salesRepsLoading && (
                    <SalesRepsLoadingSection>
                      <SvgIcon
                        name={SvgNames.Loading}
                        width={24}
                        height={24}
                        color={tokens.BRAND_HERO_BLUE}
                      />
                    </SalesRepsLoadingSection>
                  )}
                  {!salesRepsLoading && salesRepOptions.length > 1 && (
                    <MultiSelectDropdown
                      testId="sales-rep"
                      label=""
                      options={salesRepOptions}
                      allOptionText="All Sales Reps"
                      onChange={(updatedValues) => {
                        setControls((current) => ({
                          ...current,
                          salesReps: updatedValues
                            .filter((option) => option.checked)
                            .map((option) => option.id),
                        }));
                      }}
                    />
                  )}
                  {!salesRepsLoading && salesRepOptions.length <= 1 && (
                    <EmptyStateText
                      size="small"
                      color={tokens.TINTS_OFF_WHITE_20}>
                      {NO_SALES_REPS_FOUND_MESSAGE}
                    </EmptyStateText>
                  )}
                </FilterField>
              )}
            </>
          )}
          <FilterField>
            <FilterLabel size={tokens.FONT_SIZE_1}>Type of sale</FilterLabel>
            <CheckboxGroup>
              <Checkbox
                label="Sales rep action"
                checked={controls.includeSalesRepAction}
                onChange={(e) =>
                  setControls((current) => ({
                    ...current,
                    includeSalesRepAction: e.target.checked,
                  }))
                }></Checkbox>
              <Checkbox
                label="Urgent"
                checked={controls.includeUrgent}
                onChange={(e) =>
                  setControls((current) => ({
                    ...current,
                    includeUrgent: e.target.checked,
                  }))
                }></Checkbox>
              {splatUserType !== 'CX Pro' && (
                <Checkbox
                  label="Starred"
                  checked={controls.includedStarred}
                  onChange={(e) =>
                    setControls((current) => ({
                      ...current,
                      includedStarred: e.target.checked,
                    }))
                  }></Checkbox>
              )}
              {isSalesChatEnabled && (
                <Checkbox
                  label="Unread messages"
                  checked={controls.includeUnreadMessages}
                  onChange={(e) =>
                    setControls((current) => ({
                      ...current,
                      includeUnreadMessages: e.target.checked,
                    }))
                  }></Checkbox>
              )}
            </CheckboxGroup>
          </FilterField>
        </FilterBody>
        <FilterFooter>
          <Button color="secondary" onClick={() => setFiltersOpen(false)}>
            Close
          </Button>
          <Button color="primary" onClick={handleApply}>
            Apply
          </Button>
        </FilterFooter>
      </FilterContainer>
    </SlidingDrawer>
  );
};

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const FilterHeader = styled.div`
  padding: 1rem;
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const FilterBody = styled.div`
  padding: 1rem;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 1rem;

  &:first-child {
    margin-top: 0;
  }
`;

const FilterLabel = styled(Typography)`
  color: ${tokens.TINTS_OFF_WHITE_20};
`;

const FilterFooter = styled.div`
  padding: 1rem;
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;
`;

const SalesRepsLoadingSection = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const HierarchySection = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${tokens.OFFWHITE_30};
  border-radius: 8px;
  background-color: rgba(31, 38, 71, 0.04);
  overflow: hidden;
`;

const HierarchyLoadingSection = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 8px;
`;

const HierarchyError = styled(Typography)`
  padding: 0.5rem 8px 0;
`;

const EmptyStateText = styled(Typography)`
  padding: 0.5rem 0;
`;

const SelectionCountText = styled(Typography)`
  margin-top: 0.5rem;
`;

const TreeContainer = styled.div<{ maxHeight: string }>`
  display: flex;
  flex-direction: column;
  max-height: ${({ maxHeight }) => maxHeight};
  overflow: auto;
  padding: 0 8px 8px;
`;

const AllOptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 6px 0;
`;

const HierarchyCheckboxWrap = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
`;

const HierarchyRowLabel = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: 2px;
`;

export { PipelineFilterMenu };
